// chatType — lightweight enum + helpers for the "what kind of chat is
// this?" selector that appears above the composer when a fresh chat is
// started. The choice is purely a labeling/grouping aid: it drives the
// title prefix produced by `deriveChatTitle` and the section grouping
// in the Recent tab of the chat list dialog. No backend schema change
// — the type is encoded in the title prefix ("Task: …", "Quest: …",
// "Challenge: …") so existing rows keep working and we can detect the
// type from any saved row by scanning the prefix.

export type ChatType = "task" | "quest" | "challenge" | "open";

export interface ChatTypeOption {
  value: ChatType;
  label: string;
  /** Prefix prepended to derived titles. Empty for "open" (no prefix). */
  prefix: string;
  /** One-line tooltip / aria description. */
  hint: string;
}

export const CHAT_TYPES: ChatTypeOption[] = [
  {
    value: "open",
    label: "Open chat",
    prefix: "",
    hint: "Free-form — no task tracking",
  },
  {
    value: "task",
    label: "Task",
    prefix: "Task",
    hint: "A single thing you want to get done",
  },
  {
    value: "quest",
    label: "Quest",
    prefix: "Quest",
    hint: "A guided multi-step run",
  },
  {
    value: "challenge",
    label: "Challenge",
    prefix: "Challenge",
    hint: "Deeper exercise tied to your mastery path",
  },
];

/** Look up the user-facing label for a type value. */
export function chatTypeLabel(t: ChatType): string {
  return CHAT_TYPES.find((o) => o.value === t)?.label ?? "Open chat";
}

/** Detect the chat type from a stored title's prefix (case-insensitive).
 *  Recent-tab grouping uses this so legacy rows (created before this
 *  selector existed) still slot into the right bucket if their title
 *  happens to start with one of the known prefixes — e.g. a chat
 *  seeded from a quest already says "Quest: …". */
export function detectChatType(title: string | null | undefined): ChatType {
  if (!title) return "open";
  const head = title.trim().split(":")[0]?.trim().toLowerCase();
  if (head === "task") return "task";
  if (head === "quest") return "quest";
  if (head === "challenge") return "challenge";
  return "open";
}

const STORAGE_KEY = "hl.chat.lastType";

/** Remember the user's last selection across sessions so they don't
 *  have to re-pick every time. Defaults to "open" on first ever load. */
export function loadLastChatType(): ChatType {
  if (typeof window === "undefined") return "open";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "task" || raw === "quest" || raw === "challenge" || raw === "open") {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return "open";
}

export function saveLastChatType(t: ChatType) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, t);
  } catch {
    /* ignore */
  }
}